import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export function useChatAssistant({ user }: any) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<any[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState<any>(null);
  const [bookingData, setBookingData] = useState<any>({});
  const [currentStep, setCurrentStep] = useState(0);
  const messagesEndRef = useRef<any>(null);
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<any>(null);
  const [proactiveInsights, setProactiveInsights] = useState<any[]>([]);
  const [showInsights, setShowInsights] = useState(true);
  const [pulseActive, setPulseActive] = useState(false);
  const fileInputRef = useRef<any>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showIntroTooltip, setShowIntroTooltip] = useState(false);

  const scrollToBottom = () => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); };

  useEffect(() => { scrollToBottom(); }, [messages]);
  useEffect(() => { if (!isOpen) { setCurrentStep(0); setBookingData({}); } }, [isOpen]);
  useEffect(() => { if (isOpen && sessionId) loadChatHistory(); }, [isOpen, sessionId]);

  const loadChatHistory = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/history/${sessionId}`);
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages.map((msg: any) => ({ role: msg.role, content: msg.content, timestamp: new Date(msg.created_at) })));
      }
    } catch (error: any) { console.error('Error loading chat history:', error); }
  };

  const toggleVoiceInput = useCallback(() => {
    if (isListening) { recognitionRef.current?.stop(); setIsListening(false); return; }
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    const recognition = new SpeechRecognition();
    recognition.lang = 'nb-NO'; recognition.interimResults = true; recognition.continuous = false;
    recognition.onresult = (event: any) => { setInputMessage(Array.from(event.results).map((r: any) => r[0].transcript).join('')); };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
    recognitionRef.current = recognition; recognition.start(); setIsListening(true);
  }, [isListening]);

  useEffect(() => {
    if (!user) return;
    const isAdmin = user.role === 'admin' || user.role === 'booking_medarbeider';
    if (!isAdmin) return;
    const fetchInsights = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/agentic/proactive-insights`);
        if (res.ok) { const data = await res.json(); setProactiveInsights(data.insights || []); if (data.insights?.length > 0) setPulseActive(true); }
      } catch { /* silent */ }
    };
    fetchInsights();
    const interval = setInterval(fetchInsights, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  const sendMessage = async (messageText: any = null) => {
    const textToSend = messageText || inputMessage;
    if (!textToSend.trim()) return;
    setMessages((prev: any) => [...prev, { role: 'user', content: textToSend, timestamp: new Date() }]);
    setInputMessage(''); setIsTyping(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: textToSend, session_id: sessionId })
      });
      if (response.ok) {
        const data = await response.json();
        if (!sessionId) setSessionId(data.session_id);
        setMessages((prev: any) => [...prev, { role: 'assistant', content: data.response, timestamp: new Date() }]);
      } else {
        setMessages((prev: any) => [...prev, { role: 'assistant', content: t('auto.beklager_det_oppstod_en_feil_vennligst_p', 'Sorry, an error occurred. Please try again.'), timestamp: new Date() }]);
      }
    } catch (error: any) {
      console.error('Error sending message:', error);
      setMessages((prev: any) => [...prev, { role: 'assistant', content: 'Sorry, could not send the message. Check your connection.', timestamp: new Date() }]);
    } finally { setIsTyping(false); }
  };

  const handleKeyPress = (e: any) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } };

  const handleFileUpload = async (e: any) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (fileInputRef.current) fileInputRef.current.value = '';
    const isImage = file.type.startsWith('image/');
    setMessages((prev: any) => [...prev, { role: 'user', content: `${isImage ? '\ud83d\uddbc\ufe0f' : '\ud83d\udcc4'} ${file.name}${inputMessage ? `\n${inputMessage}` : ''}`, timestamp: new Date(), hasFile: true, fileName: file.name, fileType: isImage ? 'image' : 'document' }]);
    const messageText = inputMessage; setInputMessage(''); setUploadingFile(true); setIsTyping(true);
    try {
      const formData = new FormData(); formData.append('file', file); formData.append('message', messageText); formData.append('session_id', sessionId || '');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/upload-document`, { method: 'POST', body: formData });
      if (response.ok) {
        const data = await response.json(); if (!sessionId) setSessionId(data.session_id);
        setMessages((prev: any) => [...prev, { role: 'assistant', content: data.response, timestamp: new Date() }]);
      } else {
        const err = await response.json().catch(() => ({}));
        setMessages((prev: any) => [...prev, { role: 'assistant', content: err.detail || 'Beklager, kunne ikke analysere filen.', timestamp: new Date() }]);
      }
    } catch { setMessages((prev: any) => [...prev, { role: 'assistant', content: 'Feil ved opplasting. Sjekk tilkoblingen din.', timestamp: new Date() }]); }
    finally { setUploadingFile(false); setIsTyping(false); }
  };

  const parseMessageForUI = (content: any) => {
    if (content.includes('UI_START_BOOKING')) return { type: 'start_booking', content: '' };
    return { type: 'text', content };
  };

  const isAirport = (location: any) => {
    if (!location) return false;
    return ['airport', 'lufthavn', 'flyplass', 'HAU', 'BGO', 'SVG', 'OSL', 'TRD'].some(kw => location.toLowerCase().includes(kw.toLowerCase()));
  };

  const handleLocationSelect = (field: any, location: any) => {
    setBookingData((prev: any) => ({ ...prev, [field]: location }));
    if (field === 'from') setCurrentStep(1);
    else if (field === 'to') {
      setCurrentStep((isAirport(bookingData.from) || isAirport(location)) ? 3 : 4);
    }
  };

  const handleStopsComplete = (stops: any) => { setBookingData((prev: any) => ({ ...prev, stops })); setCurrentStep(2); };
  const handleFlightInfoComplete = (flightNumber: any) => { setBookingData((prev: any) => ({ ...prev, flightNumber })); setCurrentStep(4); };
  const handleDateTimeSelect = ({ date, time }: any) => { setBookingData((prev: any) => ({ ...prev, date, time })); if (date && time) setCurrentStep(5); };
  const handlePassengerSelect = (count: any) => { setBookingData((prev: any) => ({ ...prev, passengers: count })); setCurrentStep(6); };

  const handleConfirmBooking = () => {
    let message = `Please create booking:\n- From: ${bookingData.from}\n- To: ${bookingData.to}\n- Date: ${bookingData.date}\n- Time: ${bookingData.time}\n- Passengers: ${bookingData.passengers}`;
    if (bookingData.stops?.length > 0) { message += '\n- Stops along the way:'; bookingData.stops.forEach((stop: any, idx: any) => { message += `\n  ${idx + 1}. ${stop.location}`; }); }
    if (bookingData.flightNumber) message += `\n- Flynummer: ${bookingData.flightNumber}`;
    sendMessage(message); setBookingData({}); setCurrentStep(0);
  };

  useEffect(() => {
    if (!user) return;
    const seen = localStorage.getItem('agentic_ai_intro_seen');
    if (!seen) { const timer = setTimeout(() => setShowIntroTooltip(true), 2000); return () => clearTimeout(timer); }
  }, [user]);

  const dismissIntro = () => { setShowIntroTooltip(false); localStorage.setItem('agentic_ai_intro_seen', '1'); };

  return {
    t, isOpen, setIsOpen, messages, inputMessage, setInputMessage, isTyping, sessionId,
    bookingData, currentStep, messagesEndRef, isListening, proactiveInsights,
    showInsights, setShowInsights, pulseActive, setPulseActive, fileInputRef,
    uploadingFile, showIntroTooltip,
    toggleVoiceInput, sendMessage, handleKeyPress, handleFileUpload,
    parseMessageForUI, isAirport, handleLocationSelect, handleStopsComplete,
    handleFlightInfoComplete, handleDateTimeSelect, handlePassengerSelect,
    handleConfirmBooking, dismissIntro
  };
}
